/* ==========================================================================
   Scandi TR — ortak etkileşimler
   Akordeon, modal, carousel, sekmeler, görünür olunca belirme, görünürken
   oynayan videolar, kaydırmalı zaman çizelgesi, kare dizisi (canvas)
   animasyonu ve yorum ızgarası. Tema düzenleyicisiyle de uyumludur.
   ========================================================================== */

document.documentElement.classList.add('sc-js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

/* ---------- Akordeon ve modal: belge düzeyinde tek dinleyici ---------- */

function setAccordion(item, open) {
  item.classList.toggle('is-open', open);
  item.querySelector('[data-sc-acc-btn]')?.setAttribute('aria-expanded', String(open));
}

document.addEventListener('click', (event) => {
  const accButton = event.target.closest('[data-sc-acc-btn]');
  if (accButton) {
    const item = accButton.closest('[data-sc-acc-item]');
    if (!item) return;
    const open = !item.classList.contains('is-open');
    const group = item.closest('[data-sc-acc-single]');
    if (group && open) {
      group.querySelectorAll('[data-sc-acc-item].is-open').forEach((other) => {
        if (other !== item) setAccordion(other, false);
      });
    }
    setAccordion(item, open);
    return;
  }

  const opener = event.target.closest('[data-sc-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.scOpen);
    if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }

  const closer = event.target.closest('[data-sc-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Modalın dışına (karartılmış alana) tıklanınca kapat
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('sc-modal')) {
    event.target.close();
  }
});

/* ---------- Görünür olunca belirme ---------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
      )
    : null;

function initReveal(scope) {
  scope.querySelectorAll('[data-sc-reveal]:not(.is-in), [data-sc-stagger]:not(.is-in)').forEach((el) => {
    if (revealObserver && !reduceMotion) revealObserver.observe(el);
    else el.classList.add('is-in');
  });
}

/* ---------- Yalnızca ekranda görünürken oynayan videolar ---------- */

const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.intersectionRatio >= 0.6 && !document.hidden) {
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.6, 1] }
      )
    : null;

function initVideos(scope) {
  scope.querySelectorAll('[data-sc-autoplay] video').forEach((video) => {
    if (video.__scVideo) return;
    video.__scVideo = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');
    video.removeAttribute('autoplay');
    if (reduceMotion || !videoObserver) return;
    videoObserver.observe(video);
  });
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) document.querySelectorAll('[data-sc-autoplay] video').forEach((v) => v.pause());
});

/* ---------- Carousel (kaydırmalı liste + oklar + noktalar + küçük resimler) ---------- */

function initCarousel(root, signal) {
  const track = root.querySelector('[data-sc-track]');
  if (!track) return;
  const slides = [...track.children];
  if (!slides.length) return;

  const snap = root.dataset.scSnap || 'start';
  const prevButtons = [...root.querySelectorAll('[data-sc-prev]')];
  const nextButtons = [...root.querySelectorAll('[data-sc-next]')];
  const thumbs = [...root.querySelectorAll('[data-sc-thumb]')];
  const dotsWrap = root.querySelector('[data-sc-dots]');
  const dotLabel = root.dataset.scDotLabel || 'Slayt';
  let dots = [];
  let index = -1;

  if (dotsWrap) {
    dotsWrap.innerHTML = '';
    dots = slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sc-dot';
      dot.setAttribute('aria-label', `${dotLabel} ${i + 1} / ${slides.length}`);
      dot.addEventListener('click', () => goTo(i), { signal });
      dotsWrap.appendChild(dot);
      return dot;
    });
  }

  const padStart = () => parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;

  function anchorX() {
    const box = track.getBoundingClientRect();
    return snap === 'center' ? box.left + box.width / 2 : box.left + padStart();
  }

  function slideX(slide) {
    const rect = slide.getBoundingClientRect();
    return snap === 'center' ? rect.left + rect.width / 2 : rect.left;
  }

  function atStart() {
    return Math.abs(track.scrollLeft) <= 2;
  }

  function atEnd() {
    return Math.abs(track.scrollLeft) + track.clientWidth >= track.scrollWidth - 2;
  }

  function currentIndex() {
    if (atEnd() && !atStart()) return slides.length - 1;
    const anchor = anchorX();
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs(slideX(slide) - anchor);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  function goTo(i) {
    const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
    track.scrollBy({ left: slideX(target) - anchorX(), behavior: scrollBehavior });
  }

  function update() {
    const isStatic = track.scrollWidth <= track.clientWidth + 2;
    root.classList.toggle('is-static', isStatic);
    prevButtons.forEach((b) => (b.disabled = atStart()));
    nextButtons.forEach((b) => (b.disabled = atEnd()));
    const next = currentIndex();
    if (next === index) return;
    index = next;
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    root.dispatchEvent(new CustomEvent('sc:slide', { detail: { index } }));
  }

  let ticking = false;
  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      update();
    });
  };

  prevButtons.forEach((b) => b.addEventListener('click', () => goTo(currentIndex() - 1), { signal }));
  nextButtons.forEach((b) => b.addEventListener('click', () => goTo(currentIndex() + 1), { signal }));
  thumbs.forEach((thumb) =>
    thumb.addEventListener('click', () => goTo(Number(thumb.dataset.scThumb)), { signal })
  );
  track.addEventListener('scroll', schedule, { passive: true, signal });
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(schedule);
    ro.observe(track);
    signal.addEventListener('abort', () => ro.disconnect());
  } else {
    window.addEventListener('resize', schedule, { passive: true, signal });
  }

  root.scGoTo = goTo;
  update();
}

/* ---------- Sekmeler ---------- */

function initTabs(root, signal) {
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  if (!tabs.length) return;
  const panels = tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));

  function select(i, focus) {
    tabs.forEach((tab, j) => {
      const on = i === j;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      tab.classList.toggle('is-active', on);
      if (panels[j]) panels[j].hidden = !on;
    });
    if (focus) tabs[i].focus();
    tabs[i].scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: scrollBehavior });
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(i), { signal });
    tab.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        select((i + step + tabs.length) % tabs.length, true);
      },
      { signal }
    );
  });
}

/* ---------- Kaydırmalı zaman çizelgesi ("60 Günde Dönüşüm") ---------- */

function initJourney(root, signal) {
  const items = [...root.querySelectorAll('[data-sc-jny-item]')];
  const fill = root.querySelector('[data-sc-jny-fill]');
  const line = root.querySelector('[data-sc-jny-track]');
  if (!items.length || !fill || !line) return;
  const markers = items.map((item) => item.querySelector('[data-sc-jny-marker]'));
  const activation = parseFloat(root.dataset.activation) || 62;

  const centerOf = (el, top) => {
    const rect = el.getBoundingClientRect();
    return rect.top - top + rect.height / 2;
  };

  let ticking = false;
  function update() {
    ticking = false;
    const top = root.getBoundingClientRect().top;
    const trigger = (window.innerHeight * activation) / 100;
    const first = centerOf(markers[0], top);
    const last = centerOf(markers[markers.length - 1], top);
    line.style.top = `${first}px`;
    fill.style.top = `${first}px`;
    line.style.height = `${Math.max(0, last - first)}px`;

    let lastActive = null;
    items.forEach((item, i) => {
      const rect = markers[i].getBoundingClientRect();
      const active = rect.top + rect.height / 2 <= trigger;
      item.classList.toggle('is-active', active);
      if (active) lastActive = centerOf(markers[i], top);
    });
    fill.style.height = `${lastActive === null ? 0 : Math.max(0, lastActive - first)}px`;
  }

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };
  // Horizon gibi temalarda sayfa window değil bir kutu (.page-wrapper) içinde kayar;
  // capture ile her kaydırma olayını yakalarız.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
  window.addEventListener('resize', onScroll, { passive: true, signal });
  update();
}

/* ---------- Kaydırmayla oynayan kare dizisi ("Bir Scandi parçasının içine bak") ---------- */

function initSequence(root, signal) {
  const canvas = root.querySelector('canvas');
  const base = root.dataset.url;
  const count = parseInt(root.dataset.count, 10);
  const ext = root.dataset.ext || 'jpg';
  const pad = parseInt(root.dataset.pad, 10) || 3;
  if (!canvas || !base || !count) return;
  if (reduceMotion) return; // Hareket azaltma açıksa sabit ilk kare (poster) görünür

  const ctx = canvas.getContext('2d');
  const frames = new Array(count);
  const EASE = 0.12;
  // Animasyonun oynadığı kaydırma mesafesi (ekran yüksekliğinin oranı); büyüdükçe yavaşlar
  const span = Math.min(1.2, Math.max(0.5, parseFloat(root.dataset.span) || 0.6));
  let loaded = 0;
  let ready = false;
  let target = 0;
  let current = 0;
  let drawn = -1;
  let raf = null;

  const frameUrl = (i) => `${base}${String(i + 1).padStart(pad, '0')}.${ext}`;

  function progress() {
    // Animasyonun ortası, görsel ekranın tam ortasındayken oynar; span kadar kaydırmada baştan sona ilerler
    const rect = canvas.getBoundingClientRect();
    const vh = window.innerHeight;
    const center = rect.top + rect.height / 2;
    return Math.max(0, Math.min(1, (vh * (0.5 + span / 2) - center) / (vh * span)));
  }

  function draw(i) {
    let k = i;
    while (k > 0 && !(frames[k] && frames[k].complete && frames[k].naturalWidth)) k -= 1;
    const img = frames[k];
    if (k === drawn || !img || !img.naturalWidth) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    drawn = k;
  }

  function loop() {
    const diff = target - current;
    current = Math.abs(diff) < 0.001 ? target : current + diff * EASE;
    draw(Math.round(current * (count - 1)));
    raf = current === target ? null : requestAnimationFrame(loop);
  }

  function onScroll() {
    if (!ready) return;
    target = progress();
    if (raf === null) raf = requestAnimationFrame(loop);
  }

  function load() {
    for (let i = 0; i < count; i += 1) {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        loaded += 1;
        if (i === 0) {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          draw(0);
        }
        if (!ready && frames[0]?.naturalWidth && loaded >= Math.min(10, count)) {
          ready = true;
          root.classList.add('is-ready');
          onScroll();
        }
      };
      img.src = frameUrl(i);
      frames[i] = img;
    }
  }

  // Kareleri sayfa açılışında değil, bölüme yaklaşınca indir
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        load();
      },
      { rootMargin: '100% 0px' }
    );
    io.observe(root);
    signal.addEventListener('abort', () => io.disconnect());
  } else {
    load();
  }

  // Horizon gibi temalarda sayfa window değil bir kutu (.page-wrapper) içinde kayar;
  // capture ile her kaydırma olayını yakalarız.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
  window.addEventListener('resize', onScroll, { passive: true, signal });
  signal.addEventListener('abort', () => raf && cancelAnimationFrame(raf));
}

/* ---------- Yorum ızgarası (masonry) + "Daha fazla yükle" ---------- */

function initMasonry(root, signal) {
  const list = root.querySelector('[data-sc-masonry]');
  if (!list) return;
  const cards = [...list.querySelectorAll('[data-sc-review]')];
  const more = root.querySelector('[data-sc-more]');
  const counter = root.querySelector('[data-sc-counter]');
  const template = counter?.dataset.template || '[gosterilen] / [toplam]';
  const initial = parseInt(root.dataset.initial, 10) || 6;
  const step = parseInt(root.dataset.step, 10) || 4;
  const maxCols = parseInt(root.dataset.cols, 10) || 5;
  let shown = Math.min(initial, cards.length);
  let columns = [];
  let colCount = 0;

  const colsFor = (w) => Math.min(maxCols, w >= 1280 ? 5 : w >= 1024 ? 4 : w >= 640 ? 3 : 2);

  function shortest() {
    return columns.reduce((a, b) => (a.offsetHeight <= b.offsetHeight ? a : b));
  }

  function layout() {
    colCount = colsFor(window.innerWidth);
    list.classList.add('is-masonry');
    list.innerHTML = '';
    columns = Array.from({ length: colCount }, () => {
      const col = document.createElement('div');
      col.className = 'sc-rev__col';
      list.appendChild(col);
      return col;
    });
    cards.forEach((card, i) => {
      card.hidden = i >= shown;
      if (i < shown) shortest().appendChild(card);
    });
    cards.slice(shown).forEach((card) => columns[columns.length - 1].appendChild(card));
    refresh();
  }

  function refresh() {
    if (counter) {
      const total = counter.dataset.total || String(cards.length);
      counter.textContent = template.replace('[gosterilen]', String(shown)).replace('[toplam]', total);
    }
    if (more) more.hidden = shown >= cards.length;
  }

  more?.addEventListener(
    'click',
    () => {
      const next = Math.min(cards.length, shown + step);
      cards.slice(shown, next).forEach((card) => {
        card.hidden = false;
        shortest().appendChild(card);
      });
      shown = next;
      refresh();
    },
    { signal }
  );

  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (colsFor(window.innerWidth) !== colCount) layout();
      }, 150);
    },
    { passive: true, signal }
  );

  layout();
}

/* ---------- Başlatma ---------- */

const initializers = [
  ['[data-sc-carousel]', initCarousel],
  ['[data-sc-tabs]', initTabs],
  ['[data-sc-journey]', initJourney],
  ['[data-sc-sequence]', initSequence],
  ['[data-sc-reviews]', initMasonry],
];

function init(scope = document) {
  initializers.forEach(([selector, setup]) => {
    scope.querySelectorAll(selector).forEach((el) => {
      if (el.__scAbort) return;
      const controller = new AbortController();
      el.__scAbort = controller;
      try {
        setup(el, controller.signal);
      } catch (error) {
        console.error('[Scandi TR]', selector, error);
      }
    });
  });
  initReveal(scope);
  initVideos(scope);
}

init();

/* Tema düzenleyicisi: bölüm yeniden yüklenince temizle ve tekrar başlat */
document.addEventListener('shopify:section:unload', (event) => {
  event.target
    .querySelectorAll(initializers.map(([s]) => s).join(','))
    .forEach((el) => el.__scAbort?.abort());
});
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  const accItem = block.closest('[data-sc-acc-item]');
  if (accItem) setAccordion(accItem, true);
  if (block.matches('[role="tab"]')) block.click();
  const carousel = block.closest('[data-sc-carousel]');
  const slide = carousel && [...carousel.querySelector('[data-sc-track]').children].indexOf(block);
  if (carousel?.scGoTo && slide > -1) carousel.scGoTo(slide);
});
